export enum AnalyticsEvent {
  accountCreated = 'app.account.created',
  organizationCreated = 'app.organization.created',
  userCreated = 'app.user.created',
  spaceCreated = 'app.space.created',
  spaceJoined = 'app.space.joined',
  taskCreated = 'app.task.created',
  taskCompleted = 'app.task.completed',
  aiCompleted = 'app.ai.completed',
  screenOpened = 'app.screen.opened',
  importStarted = 'app.import.started',
  importCompleted = 'app.import.completed',
  importDeleted = 'app.import.deleted',
  analyticsMcpUrlCopied = 'app.analytics.mcp_url_copied',
  analyticsPromptCopied = 'app.analytics.prompt_copied',
  analyticsGuideDownloaded = 'app.analytics.guide_downloaded',
  firmographicsSubmitted = 'app.firmographics.submitted',
  sessionStarted = 'app.session.started',
}

export type AnalyticsSessionTrigger = 'load' | 'return' | 'new_day';

export type AnalyticsEventPayloads = {
  [AnalyticsEvent.accountCreated]: {
    loginMethod: 'magic_link' | 'google';
  };
  [AnalyticsEvent.organizationCreated]: {
    organizationId: number;
  };
  [AnalyticsEvent.userCreated]: object;
  [AnalyticsEvent.spaceCreated]: {
    spaceId: number;
    organizationId: number;
  };
  [AnalyticsEvent.spaceJoined]: {
    spaceId: number;
    organizationId: number;
  };
  [AnalyticsEvent.taskCreated]: {
    taskId: number;
    taskType: 'epic' | 'item' | 'subitem';
    spaceId: number;
  };
  [AnalyticsEvent.taskCompleted]: AnalyticsTaskCompletedPropertiesType;
  [AnalyticsEvent.aiCompleted]: AnalyticsAiCompletedPropertiesType;
  [AnalyticsEvent.screenOpened]: {
    screen:
      | 'backlog'
      | 'epics'
      | 'planning'
      | 'sprint'
      | 'kanban'
      | 'triage'
      | 'archive'
      | 'task'
      | 'analytics'
      | 'signup'
      | 'all_tasks'
      | 'decisions';
    // Space screens only: the saved view open on the screen (its publicId), or null when the
    // screen has none. spaceId/organizationId name the space in the URL.
    view?: string | null;
    // null for org-level screens (all_tasks, decisions), which belong to no space.
    spaceId?: number | null;
    organizationId?: number;
  };
  [AnalyticsEvent.importStarted]: {
    importId: number;
    spaceId: number;
    fileName: string;
  };
  [AnalyticsEvent.importCompleted]: {
    importId: number;
    spaceId: number;
    tasksCreatedCount: number;
  };
  [AnalyticsEvent.importDeleted]: {
    importId: number;
    spaceId: number;
  };
  [AnalyticsEvent.analyticsMcpUrlCopied]: {
    spaceId: number;
  };
  [AnalyticsEvent.analyticsPromptCopied]: {
    spaceId: number;
    promptKey: string;
  };
  [AnalyticsEvent.analyticsGuideDownloaded]: {
    spaceId: number;
  };
  // Org-level firmographics belong on an org-attributed event, not a single-valued
  // user profile property (which is last-write-wins and collapses a multi-org owner's
  // orgs into one size). organizationId keys each submission to its org.
  [AnalyticsEvent.firmographicsSubmitted]: {
    organizationId: number;
    companySize: string;
    referralSource: string;
    previousTool: string;
  };
  [AnalyticsEvent.sessionStarted]: {
    trigger: AnalyticsSessionTrigger;
    // A session is org-level: spaceId is always null, organizationId is the active organization.
    spaceId: null;
    organizationId: number | undefined;
  };
};

export type AnalyticsAiCompletedPropertiesType =
  | {
      feature: 'vibe_speccing' | 'vibe_breakdown';
      taskId: number;
      attempts: number;
    }
  | {
      feature: 'decisions';
      // 1 + the clarification questions the user answered.
      attempts: number;
      jobId: number;
      spaceId: number;
      // The job's space's org; undefined when unknown (not filled in from another space).
      organizationId: number | undefined;
      requestedCount: number;
      // The socket's decisionsCount, or the before/after count on the poll path. Omitted when
      // unknown.
      createdCount?: number;
      // Mixpanel's dedupe key, `decisions-job-<jobId>`.
      $insert_id: string;
      // Epoch seconds, the job's createdAt: identical on every tab, which dedupe also requires.
      time: number;
    };

export type AnalyticsTaskCompletedPropertiesType = {
  taskId: number;
  taskType: 'item' | 'subitem';
  organizationId: number | undefined;
  spaceId: number | undefined;
  // 'kanban' for any task in a kanban space, otherwise the task's stage.
  stage: 'backlog' | 'planning' | 'sprint' | 'kanban' | 'triage' | 'archive';
  // Omitted when the task's dependency data was not loaded.
  blockedBy?: boolean;
  blocking?: boolean;
  epic: boolean;
  owner: boolean;
  startDate: boolean;
  targetDate: boolean;
  priority: boolean;
  estimate: boolean;
  subitem: boolean;
  label: boolean;
};
